import {
  Component, EventEmitter, Inject, Input, OnDestroy, OnInit, Output, PLATFORM_ID, ViewChild
} from '@angular/core';

import {
  AbstractControl, UntypedFormBuilder, UntypedFormGroup, ValidationErrors, Validators, FormsModule, ReactiveFormsModule, FormGroup
} from '@angular/forms';

import { isPlatformBrowser, NgClass } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { Subscription } from 'rxjs';

import { BaseFormErrorTranslateService } from '../../forms/form-common-error/base-form-error-translate.service';
import { ErrorTranslateService } from '../../forms/error-translate-service';
import { SdBannerComponent } from '../../medias/banner/banner.component';
import { SdButtonComponent } from '../../buttons/button/button.component';
import { SdFormCheckboxComponent } from '../../forms/form-checkbox/form-checkbox.component';
import { SdFormCommonErrorComponent } from '../../forms/form-common-error/form-common-error.component';
import { SdFormErrorComponent } from '../../forms/form-error/form-error.component';
import { SdFormInputComponent } from '../../forms/form-input/form-input.component';
import { TypographyUtils } from '../../../utils/typography.utils';
import { INewsletter } from '../../../_interfaces/newsletter.interface';
import { ISdFormComponentItem } from '../../forms/form-component-interface.model';
import { SdFormRadioGroupComponent } from '../../forms/form-radio-group/form-radio-group.component';
import { SdHtmlComponent } from '../../html/html.component';

@Component({
  selector: 'sd-newsletter',
  templateUrl: './newsletter.component.html',
  standalone: true,
  imports: [
    SdBannerComponent,
    FormsModule,
    ReactiveFormsModule,
    NgClass,
    SdFormInputComponent,
    SdFormErrorComponent,
    SdButtonComponent,
    SdFormCommonErrorComponent,
    SdFormCheckboxComponent,
    TranslateModule,
    SdFormRadioGroupComponent,
    SdHtmlComponent
  ],
  providers: [
    { provide: BaseFormErrorTranslateService, useClass: ErrorTranslateService }
  ]
})
export class SdNewsletterComponent implements OnInit, OnDestroy {
  constructor(
    private formBuilder: UntypedFormBuilder,
    @Inject(PLATFORM_ID) private platformId: any,
  ) {}

  formValid = false;
  formSubscription: Subscription;

  @Input()
  formNewsletter: FormGroup;

  @Input()
  contactGroupName: string;

  @Input()
  genderRadio: ISdFormComponentItem[];

  getHTMLText = TypographyUtils.getHTMLText;

  @Input()
  data: INewsletter;

  @Input()
  formSuccess = false;

  @Input()
  isIntrusive = false;

  @Input()
  isInsideModal = false;

  @Input()
  isRow = true;

  @Input()
  useDQEValidation = false;

  @Input()
  suggestions = [];

  @Input()
  currentStep = 0;

  @Input() set formInputValidation(value: {
    isValid: boolean;
    errors: ValidationErrors;
  }) {
    if (this.isInsideModal) {
      this.formNewsletter?.get(this.contactGroupName).get('email')?.setErrors(value.errors);
    } else {
      this.formNewsletter?.get('formInput')?.setErrors(value.errors);
    }
  }

  @ViewChild('inputNewsletter', { static: false })
  inputNewsletter?: SdFormInputComponent;

  @ViewChild('newsletterCheckbox', { static: false })
  newsletterCheckbox?: SdFormCheckboxComponent;

  @Output()
  inputNewsletterValue = new EventEmitter<{
    phone?: string,
    gender?: string
    email: string;
    firstName?: string;
    lastName?: string;
  }>();

  @Output()
  closeModal = new EventEmitter<void>();
  @Output()
  validateInput = new EventEmitter<AbstractControl>();
  @Output()
  inputChanged = new EventEmitter<AbstractControl>();
  @Output()
  houseHoldSizeChanged = new EventEmitter<number>();

  ngOnInit() {
    if (isPlatformBrowser(this.platformId) && !this.isInsideModal) {
      this.formNewsletter = this.formBuilder.group({
        formInput: [
          '',
          this.useDQEValidation
            ? []
            : [
              Validators.required,
              Validators.pattern('^[a-z0-9._%+-]+@[a-z0-9.-]+\\.[a-z]{2,4}$'),
              Validators.minLength(4)
            ]
        ],
        formLastName: [
          '',
          [
            Validators.pattern(
              '^[\\w\'\\-,.А-Яа-я][^0-9_!¡?÷?¿/\\\\+=@#$%ˆ&*(){}|~<>;:[\\]]{1,}$',
            ),
            Validators.minLength(2)
          ]
        ],
        formFirstName: [
          '',
          [
            Validators.pattern(
              '^[\\w\'\\-,.А-Яа-я][^0-9_!¡?÷?¿/\\\\+=@#$%ˆ&*(){}|~<>;:[\\]]{1,}$',
            ),
            Validators.minLength(2)
          ]
        ],
        formCheckbox: [false, [Validators.requiredTrue]]
      });

      this.formSubscription = this.formNewsletter.valueChanges.subscribe(() => {
        this.formValid = this.formNewsletter.valid;
      });
    }
  }

  getFormControls(groupName: string): any {
    const group = this.formNewsletter?.get(groupName) as UntypedFormGroup;
    return group && !!group.controls ? Object.values(group.controls) : [];
  }

  getFormGroups(): string[] {
    const group = this.formNewsletter;
    return group ? Object.keys(group.controls) : [];
  }

  ngOnDestroy() {
    this.formSubscription?.unsubscribe();
  }

  trackControlsByName(index: number, control: any) {
    return control.name;
  }

  trackGroupByName(index: number, group: any) {
    return group.name;
  }

  onSubmit() {
    if (this.formNewsletter.valid) {
      this.inputNewsletterValue.emit({
        email: this.formNewsletter.value.formInput,
        firstName: this.formNewsletter.value.formFirstName,
        lastName: this.formNewsletter.value.formLastName
      });
    } else {
      this.formNewsletter.markAllAsTouched();
    }
  }

  onCloseModal() {
    this.closeModal.emit();
  }

  validateInputOnBlur(formInput: AbstractControl) {
    this.validateInput.emit(formInput);
  }

  onInputChanged() {
    if (this.isInsideModal) {
      this.inputChanged.emit(this.formNewsletter?.get(this.contactGroupName).get('email'));
    } else {
      this.inputChanged.emit(this.formNewsletter.get('formInput'));
    }
  }

  increaseHouseHoldSize() {
    this.houseHoldSizeChanged.emit(1);
  }

  decreaseHouseHoldSize() {
    this.houseHoldSizeChanged.emit(-1);
  }
}
